import React, { createContext, useEffect, useMemo, useState } from 'react';
import { Platform, useWindowDimensions } from 'react-native';

export enum ScreenSize {
  DesktopFull = 'desktopFull', //  >1620
  DesktopSingleColumn = 'desktopSingleColumn', // 1300 - 1620
  Tablet = 'tablet', // 1080 - 1300
  Mobile = 'mobile' // <768
}

export interface LayoutContextProps {
  children?: React.ReactElement;
}

export const LayoutContext = createContext<{
  screenSize: ScreenSize;
  isVertical: boolean;
  isMobile: boolean;
  dimensions: {
    width: number;
    height: number;
  };
}>({
  screenSize: ScreenSize.DesktopFull,
  isVertical: false,
  isMobile: false,
  dimensions: {
    width: 0,
    height: 0
  }
});

export const LayoutContextProvider = (props: LayoutContextProps) => {
  const { children } = props;
  const window = useWindowDimensions();
  const screenSize = useMemo(() => {
    if (window.width > 1300) {
      return ScreenSize.DesktopFull;
    } else if (window.width > 1080) {
      return ScreenSize.DesktopSingleColumn;
    } else if (window.width > 760) {
      return ScreenSize.Tablet;
    } else {
      return ScreenSize.Mobile;
    }
  }, [window.width]);
  const isVertical = useMemo(() => screenSize === ScreenSize.Mobile, [screenSize]);

  const dimensions = useMemo(
    () => ({
      width: window.width,
      height: window.height
    }),
    [window.width, window.height]
  );

  // After mount, if platform is web, zoom to 0.9
  useEffect(() => {
    const body = document.querySelector('body');
    if (!body) {
      return;
    }

    if (Platform.OS === 'web') {
      body.style.zoom = dimensions.width >= 1900 || dimensions.width <= 760 ? '1' : '0.9';
    }

    return () => {
      if (Platform.OS === 'web') {
        body.style.zoom = '1';
      }
    };
  }, [dimensions]);

  return (
    <LayoutContext.Provider
      value={{
        screenSize,
        isVertical,
        isMobile: screenSize === ScreenSize.Mobile,
        dimensions
      }}>
      {children}
    </LayoutContext.Provider>
  );
};
